import { useAuth } from '@clerk/nextjs';
import { useCallback, useRef } from 'react';

import { GenericFunction } from 'utils/typescript.utils';

import { useDeepCompareEffect } from 'hooks/useDeepCompareEffect';
import { useEmbedded } from 'hooks/useEmbedded';
import { useAccount } from 'restful/accounts';

import { OriginSource, useOriginSource } from './browse/useOriginSource';
import { AutomaticKeys, TrackingVariables } from './types';

type ExtendsNever<T> = [T] extends [never] ? true : false;

export const useTrackEvent = <
  TrackFn extends GenericFunction,
  TrackFnParams extends TrackingVariables<TrackFn>,
  Variables = Partial<TrackFnParams>,
  EventProps = Partial<
    Omit<
      TrackFnParams,
      ExtendsNever<Variables> extends true
        ? AutomaticKeys
        : AutomaticKeys | keyof Variables
    >
  >,
>({
  trackFn,
  variables,
}: {
  trackFn: TrackFn;
  variables: Variables;
}) => {
  const { userId } = useAuth();
  const { origin, setOrigin } = useOriginSource();

  return useCallback(
    (eventProps: EventProps, source?: OriginSource) => {
      if (source) setOrigin(source);
      trackFn({
        clerkUserId: userId,
        ...origin,
        ...variables,
        ...eventProps,
      });
    },
    [origin, setOrigin, trackFn, userId, variables],
  );
};

/**
 * Track an event once on mount and ensure its not tracked again
 */
export const useTrackEventOnce = <
  TrackFn extends GenericFunction,
  TrackFnParams extends TrackingVariables<TrackFn>,
  Variables = {
    [key in keyof TrackFnParams]?: TrackFnParams[key];
  },
>({
  trackFn,
  variables,
  withOrigin = false,
}: {
  trackFn: TrackFn;
  variables: Variables;
  withOrigin?: boolean;
}) => {
  const { userId, isLoaded: isClerkLoaded } = useAuth();
  const { account, isLoading: isLoadingAccount } = useAccount();
  const { isEmbedded, isLoaded } = useEmbedded();
  const tracked = useRef<boolean>(false);
  const { origin, removeItem } = useOriginSource();

  useDeepCompareEffect(() => {
    let timeout: null | NodeJS.Timeout = null;
    if (!isEmbedded && isLoaded && isClerkLoaded && !isLoadingAccount) {
      timeout = setTimeout(() => {
        if (!tracked.current) {
          trackFn({
            ...(withOrigin ? origin : {}),
            clerkUserId: userId,
            ...variables,
          });

          tracked.current = true;
          removeItem();
        }
      }, 250);
    }
    return () => {
      timeout && clearTimeout(timeout);
    };
  }, [
    isEmbedded,
    isLoaded,
    isClerkLoaded,
    isLoadingAccount,
    userId,
    withOrigin,
    origin,
    trackFn,
    variables,
    account,
  ]);
};
